<script setup lang="ts">
import { computed, ref } from 'vue';
import ActionButton from '@/components/buttons/ActionButton.vue';
import { useModalStore } from '@/features/overlays/modals/modalStore';
import { usePrivacy } from '@/features/privacy/usePrivacy';
import SocialBtnShare from '@/features/social-networks/SocialBtnShare.vue';
import { useSocials } from '@/features/social-networks/useSocials';
import { useUserQuery } from '@/api/queries/user/useUserQuery';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { MixpanelInstance } from '@/plugins/mixpanel/mixpanel';

const { copy } = useCopyToClipboard();
const { linkedinShare } = useSocials();
const { visibilityDetails } = usePrivacy();

const { publicUrl } = useUserQuery();
const modalStore = useModalStore();

// is copied status
const isCopied = ref(false);

const isPublic = computed(() => visibilityDetails.value.value === 3);

function copyToClipboard() {
  if (publicUrl.value) {
    copy(publicUrl.value);
    isCopied.value = true;

    setTimeout(() => {
      isCopied.value = false;
    }, 3500);

    /** REPORT **/
    MixpanelInstance.track('share_profile', {
      share_method: 'copy_profile_link',
    });
  }
}

function handleClick() {
  modalStore.toggle('privacy');
}
</script>

<template>
  <div class="flex flex-col p-6">
    <div class="flex flex-col h-full w-full">
      <div class="px-3 py-3 bg-info-background font-light flex gap-2">
        <v-icon size="1rem" icon="fa-circle-info" color="blue-darken-1"></v-icon>

        <p v-if="visibilityDetails.value === 3" class="text-af-grey-700 text-sm">
          {{ $t('candidate_account.share.panel.info.public') }}
        </p>

        <p v-else class="text-af-grey-700 text-sm">
          <span>
            {{ $t('candidate_account.share.panel.info.other.part_1') }}
          </span>
          <!-- we use a span here because we cant nest a button inside a p tag -->
          <!-- to avoid that modal should be a route so we can use a link -->
          <span
            role="button"
            tabindex="0"
            @click="handleClick"
            class="ms-1 underline font-medium text-af-blue-600"
          >
            {{ $t('candidate_account.share.panel.info.other.part_2') }}
          </span>
        </p>
      </div>

      <v-divider class="w-full my-5"></v-divider>

      <!-- Overlay to disable sharing features when not public -->
      <div :class="{ disabled: visibilityDetails.value !== 3 }"></div>

      <h4 class="mb-5 font-heading font-extrabold text-af-grey-700">
        {{ $t('candidate_account.share.panel.link.title') }}
      </h4>

      <p class="text-body-3 mb-5">{{ $t('candidate_account.share.panel.link.body') }}</p>

      <div class="flex items-center gap-4">
        <div class="py-1 px-4 text-body-3 rounded-sm min-h-8 border grid w-full">
          <span
            class="truncate"
            :class="[visibilityDetails.value === 3 ? '' : 'blur-xs select-none']"
          >
            {{ publicUrl }}
          </span>
        </div>

        <ActionButton @click="copyToClipboard" :disabled="!publicUrl || !isPublic">
          <v-icon v-if="isCopied" size="1rem" icon="fas fa-check" class="ms-2"></v-icon>
          <span v-if="isCopied">{{ $t('general.copy.copied') }}</span>
          <span v-else>{{ $t('general.copy.action') }}</span>
        </ActionButton>
      </div>

      <v-divider class="w-full my-5"></v-divider>

      <h4 class="mb-5 font-heading font-extrabold text-af-grey-700">
        {{ $t('candidate_account.share.panel.linkedin.title') }}
      </h4>

      <div class="flex justify-between items-center w-full flex-wrap gap-6">
        <p class="text-body-3 me-5">{{ $t('candidate_account.share.panel.linkedin.body') }}</p>
        <SocialBtnShare :network="linkedinShare" :disabled="!isPublic" />
      </div>
    </div>
  </div>
</template>
